import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { ContactBand, PhotoEnquiryBand } from "@/components/gisela/ClosingBands"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import { TextBand } from "@/components/gisela/TextBand"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps, PlacementPhoto } from "@/types/inertia"

interface Props {
  content: {
    hero: { heading?: string; subheading?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    intro: { body?: string }
    how_it_works: { body?: string }
    fees: { heading?: string; body?: string }
    faq: { body?: string }
    // The closing pair every page of hers ends on.
    cta?: { photo?: HeroPhoto | null; _hidden?: boolean }
    contact?: { heading?: string; body?: string; _hidden?: boolean }
  }
  /** The home page's tile slot — one card per page of her site. */
  tile_photos?: Record<string, PlacementPhoto | null>
  /** A room of hers, standing beside the short enquiry form. */
  contact_photo?: PlacementPhoto | null
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

// Dedicated Spanish-therapy landing page. English at /therapy-in-spanish,
// Spanish at /terapia-en-espanol — all copy (including the head fallbacks
// below) follows the request locale.
export default function TherapyInSpanish({
  content,
  tile_photos = {},
  contact_photo,
  section_order,
  meta,
}: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.espanol
  const tc = props.translations.gisela_pages.common
  const title = meta?.title ?? t.meta_title
  const description = meta?.description ?? t.meta_description

  const bands = presentBands(
    [
      { id: "spanish-intro", present: !!content.intro.body,
        render: (i) => <TextBand index={i} heading={t.intro_heading} body={content.intro.body} /> },
      { id: "spanish-how", present: !!content.how_it_works.body,
        render: (i) => <TextBand index={i} heading={t.how_heading} body={content.how_it_works.body} /> },
      { id: "spanish-fees", present: !!content.fees.body,
        render: (i) => <TextBand index={i} heading={content.fees.heading || tc.fees} body={content.fees.body} /> },
      { id: "spanish-faq", present: !!content.faq.body,
        render: (i) => <TextBand index={i} heading={tc.common_questions} body={content.faq.body} /> },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {/* Hero */}
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo}>
            <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
              {t.eyebrow}
            </p>
            <h1 className="text-white">{content.hero.heading || t.hero_heading}</h1>
            {content.hero.subheading && (
              <p className="mt-4 max-w-2xl text-lg text-white/80">{content.hero.subheading}</p>
            )}
          </HeroBand>
        )}

        {bands.map((band, i) => (
          <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
        ))}

        {/* The close every page of hers ends on — the invitation, the tiles
            out of it, then a room beside the short form. The ask is the free
            consultation now, rather than the enquiry form on the Contact
            page: that form is the band below, and it answers in Spanish
            wherever the page is being read in Spanish. */}
        {!sectionHidden(content.cta) && (
          <ContactBand heading={t.cta_heading} body={t.cta_body} photo={content.cta?.photo} />
        )}

        {/* This page has no tile of its own to leave out. */}
        <GiselaPageTiles photos={tile_photos} />

        {!sectionHidden(content.contact) && (
          <PhotoEnquiryBand
            heading={content.contact?.heading}
            body={content.contact?.body}
            photo={contact_photo}
          />
        )}
      </PublicLayout>
    </>
  )
}
